import { useParams, useNavigate, useLocation } from "react-router-dom"
import OrdenCompraItem from "./OrdenCompraItem"
import { useEffect, useRef, useState } from "react"
import { usePatient } from "../context/PatientContext"
import { useUser } from "../context/UserContext"

const OCS_POR_PAGINA = 10

const API = process.env.REACT_APP_API_URL

function PatientProfile() {
  const { apellidoSlug, nombreSlug } = useParams()
  const { patient, setPatient } = usePatient()
  const { user } = useUser()
  const navigate = useNavigate()
  const location = useLocation()

  const isAdmin = user?.role === "ADMIN"
  const canSeeEnfermero  = user?.role === "ADMIN" || user?.role === "OBSERVADOR"
  const canSeeSensible   = user?.role === "ADMIN" || user?.role === "OBSERVADOR"

  const [notFound, setNotFound] = useState(false)
  const [search, setSearch] = useState("")
  const [detallesAbiertos, setDetallesAbiertos] = useState(false)
  const [visibles, setVisibles] = useState(OCS_POR_PAGINA)
  const sentinelRef = useRef(null)

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const res = await fetch(`${API}/pacientes/perfil/${apellidoSlug}/${nombreSlug}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })

        if (res.status === 404) {
          setNotFound(true)
          return
        }

        const data = await res.json()
        setPatient(data)

      } catch (error) {
        console.error("Error cargando paciente", error)
        setNotFound(true)
      }
    }

    fetchPatient()

    return () => { setPatient(null) }
  }, [apellidoSlug, nombreSlug, setPatient, location.key])

  // Resetear al cambiar de paciente o al buscar — deben ir antes de los early returns
  useEffect(() => { setVisibles(OCS_POR_PAGINA) }, [patient?.id])
  useEffect(() => { setVisibles(OCS_POR_PAGINA) }, [search])

  const patientOrdenes = patient?.ordenesCompra || []
  const filteredOrdenes = patientOrdenes.filter((orden) =>
    (orden.nroOrdenCompra || "").toLowerCase().includes(search.toLowerCase())
  )
  const ordenesVisibles = search ? filteredOrdenes : filteredOrdenes.slice(0, visibles)
  const hayMas = !search && filteredOrdenes.length > visibles

  // IntersectionObserver: cuando el sentinel aparece en pantalla, carga 10 más
  useEffect(() => {
    if (!sentinelRef.current || !hayMas) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibles(v => v + OCS_POR_PAGINA) },
      { rootMargin: "150px" }
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hayMas])

  if (notFound) {
    return (
      <div className="pageContainer">
        <div className="patientNotFound column">
          <i className="fa-solid fa-circle-exclamation"></i>
          <span>Paciente no encontrado</span>
          <button onClick={() => navigate("/")}>volver al inicio</button>
        </div>
      </div>
    )
  }

  if (!patient) {
    return <div className="cargandoPaciente">Cargando paciente...</div>
  }

  return (
    <div className="pageContainer">
      <div className="patientProfile column">
        <div className="patientProfileInfoContainer row">
          <div className="patientProfileIcon">
            <i className="fa-regular fa-circle-user"></i>
          </div>
          <div className="patientProfileInfo column">
            <div className="patientProfileNameNdSearch row spacebetween">
              <div className={`patientProfileName${["BAJA", "INACTIVO"].includes(patient.estado) ? " patientProfileName--inactivo" : ""}`}>
                {patient.apellido}, {patient.nombre}
              </div>
              {isAdmin && (
                <div className="patientProfileEstado">
                  {patient.estado}
                </div>
              )}
              <div className="patientProfileSearch row alignStart">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  id="searchBoxInput"
                  name="searchBox"
                  placeholder="Buscar orden..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {canSeeSensible && (
              <div
                className={`patientProfileDetallesHeader row${detallesAbiertos ? " patientProfileDetallesHeader--abierto" : ""}`}
                onClick={() => setDetallesAbiertos(v => !v)}
              >
                <i className="fa-solid fa-chevron-right"></i>
                datos del paciente
              </div>
            )}

            <div className={`patientProfileLines${canSeeSensible && !detallesAbiertos ? "" : " patientProfileLines--abierto"}`}>

                {/* Fila 1: obra social · dni · socio · celular */}
                <div className="patientProfileLineRow">
                  {isAdmin && patient.obraSocial?.nombre && (
                    <div className="patientProfileField">
                      <i className="fa-solid fa-building-columns"></i>
                      <div><span className="title">Obra social: </span><span>{patient.obraSocial.nombre}</span></div>
                    </div>
                  )}
                  {patient.dni && (
                    <div className="patientProfileField">
                      <i className="fa-solid fa-fingerprint"></i>
                      <div><span className="title">DNI: </span><span>{patient.dni}</span></div>
                    </div>
                  )}
                  {patient.nroSocio && (
                    <div className="patientProfileField">
                      <i className="fa-regular fa-id-card"></i>
                      <div><span className="title">Socio n°: </span><span>{patient.nroSocio}</span></div>
                    </div>
                  )}
                  {canSeeSensible && patient.nroCelular && (
                    <div className="patientProfileField">
                      <i className="fa-solid fa-mobile-screen"></i>
                      <div><span className="title">Celular: </span><span>{patient.nroCelular}</span></div>
                    </div>
                  )}
                </div>

                {/* Fila 2: médico · enfermero */}
                {(patient.doctor || (canSeeEnfermero && patient.enfAsignado && patient.enfAsignado.toLowerCase() !== "sin asignar")) && (
                  <div className="patientProfileLineRow">
                    {patient.doctor && (
                      <div className="patientProfileField">
                        <i className="fa-solid fa-user-doctor"></i>
                        <div>
                          <span className="title">Médico: </span>
                          <span>
                            {patient.doctor.pdNombre}
                            {patient.doctor.pdEspecialidad && ` · ${patient.doctor.pdEspecialidad}`}
                            {patient.doctor.pdMatricula && ` · Mat. ${patient.doctor.pdMatricula}`}
                          </span>
                        </div>
                      </div>
                    )}
                    {canSeeEnfermero && patient.enfAsignado && patient.enfAsignado.toLowerCase() !== "sin asignar" && (
                      <div className="patientProfileField">
                        <i className="fa-solid fa-user-nurse"></i>
                        <div><span className="title">Enfermero: </span><span>{patient.enfAsignado}</span></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Fila 3: domicilio */}
                {(patient.domicilio || patient.localidad || patient.provincia) && (
                  <div className="patientProfileLineRow">
                    <div className="patientProfileField">
                      <i className="fa-solid fa-location-dot"></i>
                      <div>
                        <span className="title">Domicilio: </span>
                        <span>{[patient.domicilio, patient.localidad, patient.provincia].filter(Boolean).join(", ")}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fila 4: productos */}
                {patient.productos && patient.productos.length > 0 && (
                  <div className="patientProfileProductos">
                    <div className="patientProfileProductosLabel">
                      <i className="fa-solid fa-box"></i>
                      <span className="title">Productos</span>
                    </div>
                    <div className="patientProfileProductosList">
                      {patient.productos.map((pp, i) => (
                        <div key={i} className="patientProfileProductoItem">
                          <span className="productoMarca">{pp.producto.marca}</span>
                          <strong style={{ fontSize: "inherit" }}>{pp.producto.codigo}</strong>
                          <span className="productoDesc">{pp.producto.descripcion}</span>
                          <span className="productoCantidad">{pp.cantidad} caja/s por {pp.producto.unidad} unidades</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

            {isAdmin && patient.observaciones && patient.observaciones !== "Sin observaciones" && detallesAbiertos && (
              <div className="patientProfileObservaciones">
                <i className="fa-solid fa-comment-dots"></i>
                <span className="title">Observaciones:</span>
                <span>{patient.observaciones}</span>
              </div>
            )}
          </div>
        </div>

        <div className="patientProfileOrdenCompraList column">
          {filteredOrdenes.length === 0 ? (
            <div className="patientProfileOCempty">
              No se encontraron órdenes
            </div>
          ) : (
            ordenesVisibles.map((orden) => (
              <OrdenCompraItem key={orden.id} orden={orden} canSeePedido={canSeeEnfermero} canSeeSensible={canSeeSensible} />
            ))
          )}
          {hayMas && (
            <div ref={sentinelRef} className="patientProfileOCloader">
              cargando más...
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PatientProfile
